// src/hooks/useUrl.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import {
  decodeQuery,
  encodeQuery,
  getDomain,
  getUrlEndpoint,
  getParams,
  getQuery,
  getUrl,
  updateEndpoint,
  updateQuery,
  mergeUrlParamsQuery,
  removeUrlParams,
} from 'utils/urlUtils';
import useZustand from 'hooks/useZustand';
import { useLocation } from 'react-router-dom';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// HOOKS
// *******************************************************************************
const useUrl = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');
  const storedCurrentPage = useZustand('module', 'currentPage');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const location = useLocation();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. COMPUTED VALUES (synchronous - available on first render)
  // ─────────────────────────────────────────────────────────────────────────────

  // CRITICAL: Compute params synchronously using useMemo instead of useEffect.
  // This ensures params are available on the first render, preventing race
  // conditions where useApi would make requests without the _id from URL.
  const params = useMemo(() => {
    const urlModule = storedModule?.url;
    const urlEndpoint = getUrlEndpoint();
    const urlEndpointSchema = storedCurrentPage?.routing?.endpoint;
    const urlSchema = urlEndpointSchema ? `${urlModule}${urlEndpointSchema}` : urlModule;
    return getParams(urlEndpoint, urlSchema);
  }, [storedModule?.url, storedCurrentPage?.routing?.endpoint, location.pathname]);

  const apiUrl = useMemo(() => {
    return storedCurrentPage?.api?.url || null;
  }, [storedCurrentPage?.api?.url]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. BUILD STATE OBJECT
  // ─────────────────────────────────────────────────────────────────────────────
  const state = useMemo(
    () => ({
      // states
      url: getUrl(),
      domain: getDomain(),
      endpoint: getUrlEndpoint(),
      api: apiUrl,
      query: getQuery(),
      params: params || {},

      // functions
      updateQuery,
      updateEndpoint,
      mergeUrlParamsQuery,
      encodeQuery,
      decodeQuery,
      removeUrlParams,
    }),
    [apiUrl, params, location.pathname, location.search]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  return state;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useUrl;

// *******************************************************************************
// HELPERS
// *******************************************************************************
